import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { X, ShieldCheck, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';

export default function QCAuditModal({ isOpen, onClose, variant, order, completedPacking, onSaved }) {
  const { session, user } = useSession() || {};
  const isDeveloper = session?.user?.role === 'developer' || user?.nama?.toLowerCase().includes('fadli') || user?.role === 'developer';

  const rawName = variant?.product_name || variant?.variant_name || 'Varian Produk';
  const labelSize = variant?.botol_ukuran_aktual || variant?.botol_ukuran_label || variant?.botol_ukuran_client || variant?.ukuran_botol_ml || 0;
  const variantDisplayName = labelSize ? `${rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim()} (${labelSize}ml)` : rawName;

  const targetPcs = Number(completedPacking) > 0 ? Number(completedPacking) : (Number(variant?.target_qty || variant?.qty) || 0);

  const [checklist, setChecklist] = useState({
    fisik: true,
    volume: true,
    label: true,
    segel: true,
  });

  const [qtyLolos, setQtyLolos] = useState(targetPcs);
  const [catatanReject, setCatatanReject] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (variant?.qc_data) {
      const qd = variant.qc_data;
      setChecklist(qd.checklist || { fisik: true, volume: true, label: true, segel: true });
      setQtyLolos(qd.qty_lolos ?? targetPcs);
      setCatatanReject(qd.catatan_reject || '');
    } else {
      setQtyLolos(targetPcs);
      setCatatanReject('');
    }
  }, [variant, targetPcs]);

  if (!isOpen || !variant) return null;

  const qtyReject = Math.max(0, targetPcs - Number(qtyLolos || 0));
  const allChecklistPassed = Object.values(checklist).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Number(qtyLolos) < 0 || Number(qtyLolos) > targetPcs) {
      toast.error(`Kuantitas lolos QC harus di antara 0 dan ${targetPcs} pcs.`);
      return;
    }

    try {
      setIsSubmitting(true);
      const inspectorName = session?.user?.name || user?.nama || 'Developer';
      const now = new Date().toISOString();

      const qcPayload = {
        status: qtyReject === 0 && allChecklistPassed ? 'passed' : 'passed_with_rejects',
        qty_target: targetPcs,
        qty_lolos: Number(qtyLolos),
        qty_reject: qtyReject,
        checklist,
        catatan_reject: catatanReject,
        dikonfirmasi_oleh: inspectorName,
        dikonfirmasi_pada: now,
      };

      // Update PO products
      const updatedProducts = (order.products || []).map(p => {
        const pName = p.product_name || p.variant_name;
        if (pName === variant.product_name || pName === variant.variant_name) {
          return {
            ...p,
            qc_data: qcPayload
          };
        }
        return p;
      });

      try {
        await base44.entities.ProductionOrder.update(order.id, { products: updatedProducts });
      } catch (poErr) {
        await base44.entities.ClientPO.update(order.id, { products: updatedProducts });
      }

      // Auto-log to RejectLog if reject > 0
      if (qtyReject > 0) {
        try {
          await base44.entities.RejectLog.create({
            po_id: order.id,
            no_po: order.no_po || order.invoice_number,
            nama_brand: order.customerName || order.nama_brand,
            nama_produk: variantDisplayName,
            tahap_proses: 'QC (Quality Control)',
            jumlah_reject: qtyReject,
            alasan_reject: catatanReject || 'Gagal inspeksi QC',
            pic_pemeriksa: inspectorName,
            created_date: now,
          });
        } catch (rErr) {
          console.warn('RejectLog auto-create warning:', rErr);
        }
      }

      toast.success(`Audit QC untuk ${variantDisplayName} berhasil disimpan!`);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving QC Audit:', err);
      toast.error('Gagal menyimpan hasil Audit QC: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 -xs flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-amber-950 to-slate-900 p-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base leading-tight">Konfirmasi QC Varian</h3>
              <p className="text-xs text-amber-200/80 font-medium">{variantDisplayName}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-slate-400 hover:text-white hover:bg-white/10 rounded-full">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          
          {/* QC Notice */}
          <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-2xl p-3 flex items-center justify-between text-xs">
            <span className="text-emerald-900 font-semibold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Pemeriksaan Quality Control (QC)
            </span>
            <Badge className="bg-emerald-600 text-white font-extrabold text-[10px]">
              Tim QC / Supervisor
            </Badge>
          </div>

          {/* Checklist Kelayakan */}
          <div className="space-y-3 p-4 bg-muted/20 rounded-2xl border border-slate-200">
            <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Checklist Kualitas Fisik & Kemasan
            </h4>
            <div className="space-y-2.5 text-xs text-slate-700">
              <div className="flex items-center space-x-2 bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <Checkbox id="c-fisik" checked={checklist.fisik} onCheckedChange={v => setChecklist(p => ({ ...p, fisik: !!v }))} />
                <Label htmlFor="c-fisik" className="font-semibold cursor-pointer text-xs">Fisik Botol & Spray Berfungsi Normal (Tutup kencang & no lecet)</Label>
              </div>

              <div className="flex items-center space-x-2 bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <Checkbox id="c-volume" checked={checklist.volume} onCheckedChange={v => setChecklist(p => ({ ...p, volume: !!v }))} />
                <Label htmlFor="c-volume" className="font-semibold cursor-pointer text-xs">Volume Isi Presisi (Tidak kurang / bocor)</Label>
              </div>

              <div className="flex items-center space-x-2 bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <Checkbox id="c-label" checked={checklist.label} onCheckedChange={v => setChecklist(p => ({ ...p, label: !!v }))} />
                <Label htmlFor="c-label" className="font-semibold cursor-pointer text-xs">Label Brand & Exp Date Terpasang Rapi & Presisi</Label>
              </div>

              <div className="flex items-center space-x-2 bg-white p-2.5 rounded-xl border border-slate-100 shadow-2xs">
                <Checkbox id="c-segel" checked={checklist.segel} onCheckedChange={v => setChecklist(p => ({ ...p, segel: !!v }))} />
                <Label htmlFor="c-segel" className="font-semibold cursor-pointer text-xs">Kebersihan Botol & Segel Plastik Utuh</Label>
              </div>
            </div>
          </div>

          {/* Audit Kuantitas */}
          <div className="p-4 bg-slate-900 text-white rounded-2xl space-y-3 shadow-sm">
            <h4 className="text-xs font-black uppercase tracking-wider text-amber-300">Hasil Audit Kuantitas (Pcs)</h4>
            
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-2.5 bg-white/10 rounded-xl border border-white/10">
                <span className="text-[10px] text-slate-400 font-bold uppercase block">Target (Pengemasan)</span>
                <span className="text-base font-black text-white">{targetPcs} <span className="text-xs font-normal">pcs</span></span>
              </div>

              <div className="p-2.5 bg-emerald-950/80 border border-emerald-500/30 rounded-xl">
                <span className="text-[10px] text-emerald-400 font-bold uppercase block">Lolos QC</span>
                <Input
                  type="number"
                  min="0"
                  max={targetPcs}
                  value={qtyLolos}
                  onChange={e => setQtyLolos(e.target.value)}
                  className="h-8 text-center font-black text-sm bg-emerald-900/60 border-emerald-400/50 text-emerald-200 mt-1"
                />
              </div>

              <div className="p-2.5 bg-rose-950/80 border border-rose-500/30 rounded-xl">
                <span className="text-[10px] text-rose-400 font-bold uppercase block">Reject / Cacat</span>
                <span className="text-base font-black text-rose-300 mt-1 block">{qtyReject} <span className="text-xs font-normal">pcs</span></span>
              </div>
            </div>

            {qtyReject > 0 && (
              <div className="space-y-1 pt-1">
                <label className="text-[11px] font-bold text-rose-300 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Catatan Penyebab Reject QC:
                </label>
                <Input
                  type="text"
                  value={catatanReject}
                  onChange={e => setCatatanReject(e.target.value)}
                  placeholder="Misal: 2 botol gores pada permukaan stiker..."
                  className="h-8 text-xs bg-white text-slate-900 font-medium"
                  required
                />
              </div>
            )}
          </div>

          {/* Footer Action */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-xs">
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isDeveloper}
              className="h-9 text-xs font-bold gap-1.5 bg-[#8C6208] text-white hover:bg-amber-900 shadow-sm"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Simpan Audit QC Varian</span>
            </Button>
          </div>

        </form>

      </div>
    </div>
  );
}
